import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Chip, Drawer, Grid2 as Grid, IconButton, Link, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  fetchMetaCreativeDetail, fetchMetaCreatives, fetchMetaOverview, metaThumbnailUrl,
  type MetaCreativeStat, type MetaRange,
} from '../../api/meta';
import { PageHeader } from '../../components/PageHeader';
import { DataState } from '../../components/DataState';
import { formatMetric } from '../../components/formatters';
import { MetaAnalysisPanel, MetaCopyPanel } from './MetaAiPanels';
import { MetaForecastPanel } from './MetaForecastPanel';
import { MetaCampaignsPanel } from './MetaCampaignsPanel';
import { MetaDrivePanel } from './MetaDrivePanel';

type RangeDays = 7 | 28 | 90;

function rangeForDays(days: RangeDays): MetaRange {
  const to = new Date();
  to.setDate(to.getDate() - 1);
  const from = new Date(to);
  from.setDate(from.getDate() - (days - 1));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

type SortKey = 'spend' | 'roas' | 'linkCtr' | 'cpc' | 'cpm' | 'cpa' | 'purchases' | 'hookRate' | 'frequency';

const COLUMNS: { key: SortKey; label: string; hint?: string }[] = [
  { key: 'spend', label: 'Spend' },
  { key: 'roas', label: 'ROAS', hint: 'Meta-attributed' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'cpa', label: 'CPA' },
  { key: 'linkCtr', label: 'Link CTR' },
  { key: 'cpc', label: 'CPC' },
  { key: 'cpm', label: 'CPM' },
  { key: 'hookRate', label: 'Hook rate', hint: 'video p25 / impressions' },
  { key: 'frequency', label: 'Freq.' },
];

const fmt = {
  currency: (v: number | null) => (v === null ? '—' : formatMetric(v, 'currency', { compact: true })),
  currency2: (v: number | null) => (v === null ? '—' : formatMetric(v, 'currency')),
  ratio: (v: number | null) => (v === null ? '—' : formatMetric(v, 'ratio')),
  percent: (v: number | null) => (v === null ? '—' : formatMetric(v, 'percent')),
  count: (v: number | null) => (v === null ? '—' : formatMetric(v, 'count')),
  freq: (v: number | null) => (v === null ? '—' : v.toFixed(2)),
};

function cellValue(stat: MetaCreativeStat, key: SortKey): string {
  switch (key) {
    case 'spend': return fmt.currency(stat.spend);
    case 'roas': return fmt.ratio(stat.roas);
    case 'purchases': return fmt.count(stat.purchases);
    case 'cpa': return fmt.currency2(stat.cpa);
    case 'linkCtr': return fmt.percent(stat.linkCtr);
    case 'cpc': return fmt.currency2(stat.cpc);
    case 'cpm': return fmt.currency2(stat.cpm);
    case 'hookRate': return fmt.percent(stat.hookRate);
    case 'frequency': return fmt.freq(stat.frequency);
  }
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h6">{value}</Typography>
    </Paper>
  );
}

function Thumb({ creativeId, format, hasThumbnail }: { creativeId: string; format: string; hasThumbnail: boolean }) {
  // Thumbnails are served from our same-origin proxy (pipeline-cached bytes), so
  // no referrerPolicy is needed. A stray 404/expired entry degrades to the
  // placeholder instead of a broken-image icon.
  const [failed, setFailed] = useState(false);
  if (!hasThumbnail || failed) {
    return (
      <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: 'action.hover', display: 'grid', placeItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">{format === 'video' ? '▶' : '—'}</Typography>
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={metaThumbnailUrl(creativeId)}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', bgcolor: 'action.hover' }}
    />
  );
}

function CreativeDrawer({ creativeId, range, onClose }: { creativeId: string; range: MetaRange; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ['meta-creative', creativeId, range.from, range.to],
    queryFn: ({ signal }) => fetchMetaCreativeDetail(creativeId, range, signal),
  });

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Creative detail</Typography>
          <IconButton onClick={onClose} aria-label="Close"><CloseRoundedIcon /></IconButton>
        </Stack>

        <DataState loading={detailQuery.isLoading} error={detailQuery.error} data={detailQuery.data}>
          {(data) => (
            <Stack spacing={2}>
              {data.creative ? (
                <>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Thumb
                      creativeId={data.creative.id}
                      format={data.creative.format}
                      hasThumbnail={data.creative.hasThumbnail ?? true}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2">{data.creative.title ?? data.creative.name}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Chip size="small" label={data.creative.format} />
                        {data.creative.cta && <Chip size="small" variant="outlined" label={data.creative.cta} />}
                      </Stack>
                    </Box>
                  </Stack>
                  {data.creative.body && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{data.creative.body}</Typography>
                  )}
                  {data.creative.linkUrl && (
                    <Link href={data.creative.linkUrl} target="_blank" rel="noreferrer" variant="body2">
                      {data.creative.linkUrl}
                    </Link>
                  )}
                  {(data.creative.bodies?.length ?? 0) > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      Dynamic creative: {data.creative.bodies!.length} body variants, {data.creative.titles?.length ?? 0} title variants.
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Creative content not synced (ad predates collection or was deleted in Meta).
                </Typography>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Ads using this creative ({data.ads.length})</Typography>
                <Stack spacing={0.5}>
                  {data.ads.map((ad) => (
                    <Stack key={ad.id} direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={ad.status}
                        color={ad.status === 'ACTIVE' ? 'success' : 'default'}
                        variant="outlined"
                      />
                      <Typography variant="body2" noWrap title={ad.adsetName ?? undefined}>{ad.name}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Daily performance</Typography>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Spend</TableCell>
                        <TableCell align="right">Purchases</TableCell>
                        <TableCell align="right">ROAS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...data.daily].reverse().map((d) => (
                        <TableRow key={d.date}>
                          <TableCell>{d.date}</TableCell>
                          <TableCell align="right">{fmt.currency2(d.spend)}</TableCell>
                          <TableCell align="right">{fmt.count(d.purchases)}</TableCell>
                          <TableCell align="right">{fmt.ratio(d.roas)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          )}
        </DataState>
      </Box>
    </Drawer>
  );
}

export function MetaAdsPage() {
  const [days, setDays] = useState<RangeDays>(28);
  const [sortKey, setSortKey] = useState<SortKey>('spend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);

  const range = useMemo(() => rangeForDays(days), [days]);

  const overviewQuery = useQuery({
    queryKey: ['meta-overview', range.from, range.to],
    queryFn: ({ signal }) => fetchMetaOverview(range, signal),
  });

  const creativesQuery = useQuery({
    queryKey: ['meta-creatives', range.from, range.to],
    queryFn: ({ signal }) => fetchMetaCreatives(range, signal),
  });

  const sorted = useMemo(() => {
    const rows = creativesQuery.data?.creatives ?? [];
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return (av - bv) * dir;
    });
  }, [creativesQuery.data, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(0);
  }

  const rowsPerPage = 25;
  const paged = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totals = overviewQuery.data?.overview.totals;

  return (
    <Box>
      <PageHeader
        title="Meta Ads"
        subtitle="Creative performance — Meta-attributed figures (company revenue truth stays with Sublime)"
        actions={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_, v) => { if (v) { setDays(v); setPage(0); } }}
            aria-label="Date range"
          >
            <ToggleButton value={7}>7 days</ToggleButton>
            <ToggleButton value={28}>28 days</ToggleButton>
            <ToggleButton value={90}>90 days</ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {totals && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}><StatTile label="Spend" value={fmt.currency(totals.spend)} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatTile label="ROAS (Meta)" value={fmt.ratio(totals.roas)} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatTile label="Purchases" value={fmt.count(totals.purchases)} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatTile label="Active creatives" value={fmt.count(totals.activeCreatives)} /></Grid>
        </Grid>
      )}

      <DataState
        loading={creativesQuery.isLoading}
        error={creativesQuery.error}
        data={creativesQuery.data}
        isEmpty={(d) => d.creatives.length === 0}
        emptyMessage="No Meta ads data for this period yet. The pipeline may still be backfilling."
      >
        {() => (
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Creative</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell align="right">Ads</TableCell>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.key} align="right" title={col.hint}>
                        <TableSortLabel
                          active={sortKey === col.key}
                          direction={sortKey === col.key ? sortDir : 'desc'}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((stat) => (
                    <TableRow
                      key={stat.creativeId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelected(stat.creativeId)}
                    >
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Thumb creativeId={stat.creativeId} format={stat.format} hasThumbnail={stat.hasThumbnail} />
                          <Typography variant="body2" noWrap title={stat.title ?? stat.name}>
                            {stat.title ?? stat.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell><Chip size="small" variant="outlined" label={stat.format} /></TableCell>
                      <TableCell align="right">{stat.adCount}</TableCell>
                      {COLUMNS.map((col) => (
                        <TableCell key={col.key} align="right">{cellValue(stat, col.key)}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sorted.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
            />
          </Paper>
        )}
      </DataState>

      <Stack spacing={3} sx={{ mt: 3 }}>
        <MetaAnalysisPanel range={range} />
        <MetaCopyPanel />
        <MetaForecastPanel />
        <MetaDrivePanel />
        <MetaCampaignsPanel />
      </Stack>

      {selected && <CreativeDrawer creativeId={selected} range={range} onClose={() => setSelected(null)} />}
    </Box>
  );
}
